// Capa platform — único punto del frontend que toca APIs de filesystem
// del browser (File API, URL, Blob, descarga).
//
// El resto del UI debe importar de aquí, no de las APIs nativas
// directamente. Así, si mañana cambian (o queremos volver a Tauri u otro
// runtime), tocamos un solo archivo.

/**
 * Lee el contenido de texto de un File object.
 * Usado para JSON y SRT que ingiere la app.
 */
export async function readTextFromFile(file: File): Promise<string> {
  return await file.text();
}

/**
 * Convierte un File en una URL que el <audio> puede consumir.
 *
 * El object URL apunta a un Blob en memoria del browser. El elemento
 * <audio> hace Range requests internamente sobre ese blob, sin pasar
 * por la red. Esto reemplaza completamente el sidecar Rust que teníamos
 * en Tauri para servir archivos con Accept-Ranges.
 *
 * IMPORTANTE: cada llamada crea una URL nueva que ocupa memoria mientras
 * exista. Cuando ya no la necesites (cambio de archivo, reset, etc.),
 * llama a revokeAudioUrl para liberar.
 */
export function audioFileToUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Libera una object URL creada con audioFileToUrl. Después de revoke,
 * la URL deja de funcionar y el browser puede liberar la memoria del Blob.
 */
export function revokeAudioUrl(url: string): void {
  try {
    URL.revokeObjectURL(url);
  } catch {
    // Silencio: revoke no debe romper nada si la URL ya fue revocada.
  }
}

/**
 * Lanza la descarga de un texto como archivo, mostrando el diálogo nativo
 * "Guardar como" del browser (con el filename sugerido).
 *
 * Es el reemplazo del saveFileDialog + writeTextFile de Tauri.
 */
export function downloadTextAsFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Diferimos el revoke para no abortar la descarga prematuramente.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Sugerencia de filename para el SRT etiquetado, basado en el archivo SRT
 * original. "meeting.srt" -> "meeting-tagged.srt".
 *
 * Si el original no termina en .srt, le agrega -tagged.srt al final.
 */
export function suggestTaggedSrtFilename(originalFilename: string | null): string {
  if (!originalFilename) return "meeting-tagged.srt";
  const lower = originalFilename.toLowerCase();
  if (lower.endsWith(".srt")) {
    return `${originalFilename.slice(0, -4)}-tagged.srt`;
  }
  return `${originalFilename}-tagged.srt`;
}

// Tipos de archivos que la app acepta. Usado en los <input type="file">
// como hint del filtro nativo del OS, aunque no es enforcement real
// (el usuario puede subir cualquier cosa, validamos después).
export const ACCEPT = {
  oracleJson: "application/json,.json",
  srt: ".srt,text/plain",
  wav: "audio/wav,audio/x-wav,.wav",
} as const;

// ---------------------------------------------------------------------------
// Fase 59 — Generación de .eml y descarga ZIP
// ---------------------------------------------------------------------------

/**
 * Genera un archivo .eml (RFC 5322) a partir de un EmailDraft.
 *
 * `From` se deja vacío para que el cliente de correo del usuario lo complete.
 * El body se incluye como text/plain UTF-8 sin quoted-printable.
 */
export function generateEml(draft: {
  to: string;
  toName: string;
  subject: string;
  body: string;
}): string {
  const toHeader = draft.to
    ? `${draft.toName} <${draft.to}>`
    : draft.toName;

  const date = new Date().toUTCString().replace("GMT", "+0000");

  return [
    "From: ",
    `To: ${toHeader}`,
    `Subject: ${draft.subject}`,
    `Date: ${date}`,
    "MIME-Version: 1.0",
    "Content-Type: text/plain; charset=UTF-8",
    "",
    draft.body,
  ].join("\r\n");
}

/** Descarga un string como archivo con el MIME type dado. */
export function downloadBlob(
  filename: string,
  content: string,
  mimeType: string,
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Descarga múltiples archivos .eml como un ZIP.
 * Usa fflate para crear el ZIP en memoria (sin compresión).
 */
export async function downloadBlobAsZip(
  files: Array<{ filename: string; content: string }>,
  zipName: string,
): Promise<void> {
  const { zipSync, strToU8 } = await import("fflate");

  const zipData: Record<string, Uint8Array> = {};
  for (const f of files) {
    zipData[f.filename] = strToU8(f.content);
  }

  const buf = zipSync(zipData);
  const blob = new Blob([buf], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = zipName;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
